import { getDomainConfigValue } from '@src/model/config/helpers';
import { type FC, memo, useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation } from 'react-router';

type DsOptions = {
    window?: Window;
    id?: string; // internal id suffix for iframe name/id
    html?: string; // full HTML to write into iframe
    p?: string | number; // DistroScale placement id
    width?: number; // optional fixed width
    height?: number; // optional fixed height
};

// Friendly-iframe creator adapted from DistroScale sample
function createDsIframe(target: HTMLElement | string, opts: DsOptions) {
    const iwin = opts?.window ?? window;
    const idSuffix = opts?.id ?? Math.random().toString(36).slice(-4);
    // Base HTML includes script tag with placeholder for p param and default anchor div
    let html =
        opts?.html ||
        atob(
            'PCFET0NUWVBFIEhUTUw+PGh0bWwgbGFuZz0iZW4tdXMiPjxoZWFkPjxtZXRhIGh0dHAtZXF1aXY9IkNvbnRlbnQtdHlwZSIgY29udGVudD0idGV4dC9odG1sOyBjaGFyc2V0PXV0Zi04Ij48c3R5bGUgdHlwZT0idGV4dC9jc3MiPip7cGFkZGluZzowO21hcmdpbjowfTwvc3R5bGU+PC9oZWFkPjxib2R5PjxzY3JpcHQgYXN5bmMgdHlwZT0idGV4dC9qYXZhc2NyaXB0IiBzcmM9Ii8vYy5qc3Jkbi5jb20vcy9jcy5qcz9wPV9fRFNfQVJHU19fIj48L3NjcmlwdD48ZGl2IGlkPSJkc19kZWZhdWx0X2FuY2hvciI+PC9kaXY+PC9ib2R5PjwvaHRtbD4='
        );
    const p = opts?.p;
    const width = opts?.width;
    const height = opts?.height;

    // Resolve target
    let container: HTMLElement | null = null;
    if (typeof target === 'string') {
        container = iwin.document.querySelector(target) as HTMLElement | null;
        if (!container) container = iwin.document.getElementById(target) as HTMLElement | null;
    } else {
        container = target;
    }
    if (!container || !container.appendChild) return null;

    if (p != null) html = html.replace(/__DS_ARGS__/g, String(p));

    const iframe = iwin.document.createElement('iframe');
    iframe.className = 'dvp_iframe';
    iframe.id = `dvp_iframe_${idSuffix}`;
    iframe.setAttribute('name', iframe.id);
    iframe.setAttribute('frameborder', '0');
    iframe.setAttribute('marginheight', '0');
    iframe.setAttribute('marginwidth', '0');
    iframe.setAttribute('scrolling', 'no');
    if (width && width > 0) {
        iframe.setAttribute('width', String(width));
        iframe.style.width = `${width}px`;
    }
    if (height && height > 0) {
        iframe.setAttribute('height', String(height));
        iframe.style.height = `${height}px`;
    }
    iframe.style.border = 'none';
    iframe.style.display = 'block';
    iframe.style.margin = '0 auto';
    iframe.src = 'about:blank';
    iframe.srcdoc = html;
    container.appendChild(iframe);

    return iframe as HTMLIFrameElement;
}

export interface DistroScaleProps {
    className?: string;
}

const DistroScale: FC<DistroScaleProps> = props => {
    const { className = 'w-full mx-auto' } = props;

    const width = 640;
    const height = 360;

    // Get config from domain config
    const distroScaleConfig = getDomainConfigValue('distroScale');
    const placementId = distroScaleConfig?.id;

    const location = useLocation();

    const containerRef = useRef<HTMLDivElement | null>(null);
    const iframeRef = useRef<HTMLIFrameElement | null>(null);

    // Create/recreate the friendly iframe on route changes
    useLayoutEffect(() => {
        if (!placementId || !containerRef.current) return;
        console.log(`[DistroScale] Creating iframe for placementId: ${placementId}`);

        // Create new iframe
        const iframe = createDsIframe(containerRef.current, { p: placementId, width, height });
        iframeRef.current = iframe;
        return () => {
            // On cleanup (route change/unmount), remove iframe to avoid duplicates
            iframeRef.current?.remove();
            iframeRef.current = null;
        };
    }, [location.pathname, placementId]);

    if (!placementId) return null;
    return <div id="DistroScale-container" ref={containerRef} className={className} />;
};

export default memo(DistroScale);
